import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { renderLexicalRichText } from "../../../utility/lexical-viewer";
import DOMPurify from "dompurify";
import {
  shortenContent,
  shortenContent10,
} from "../../../utility/format-content";
import { getCurrencySymbol } from "../../../utility/currencyUtils";
import apiInstance from "../../../utility/apiInstance";
import { Program } from "../../../core/types/types";
import { useSelector } from "react-redux";
import ApplyPopup from "../all-programs/applyPopup";
import BackendImage from "../../../core/common/backendImage";

const ProgramDetails = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );
  const { id } = useParams();
  const [program, setProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const [applyPopup, setApplyPopup] = useState<boolean>(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);

  const formatDate = (date?: string | Date) => {
    if (!date) return "-";
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("en-US", dateOptions);
  };

  /**
   * Formats a raw phone number into a human-readable international format.
   * e.g. "+61879436660" → "+61 8794 36660"
   *      "0412345678"   → "0412 345 678"
   */
  const formatPhoneNumber = (raw?: string): string => {
    if (!raw) return "";
    const digits = raw.replace(/\D/g, "");
    if (!digits) return raw;

    // International format: starts with + or country code
    if (raw.startsWith("+")) {
      // Split after country code (1–3 digits), then group remaining in 4-4 or 4-5
      const countryCodeMatch = digits.match(/^(\d{1,3})(\d{4})(\d{3,5})(\d*)$/);
      if (countryCodeMatch) {
        const [, cc, g1, g2, g3] = countryCodeMatch;
        return [`+${cc}`, g1, g2, g3].filter(Boolean).join(" ");
      }
      return `+${digits}`;
    }

    // Local format: group as blocks of 4-3-3 or similar
    if (digits.length === 10) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 11) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }

    // Fallback: group into blocks of 4
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  };

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const programsRes = await apiInstance.get(`/api/programs/${id}`);
        setProgram(programsRes.data);
      } catch (err) {
        console.error("Failed to fetch program details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProgramDetails();
  }, [id]);

  

  if (loading) return <p>Loading...</p>;
  if (!program) return <p>Program not found.</p>;

  return (
      <>
          <div className="cs-page-header">
              <button
                  onClick={() => navigate(-1)}
                  className="btn btn-outline-secondary btn-sm mb-3 d-inline-flex align-items-center gap-1"
              >
                  <i className="feather-arrow-left" />
                  Back
              </button>
              <h2>{program.programName}</h2>
              <div className="cs-page-header-content">
                  <div>{program.institution?.location || ""}</div>
                  {program.institution?.PhoneNumber && (
                      <div>{formatPhoneNumber(program.institution.PhoneNumber)}</div>
                  )}
              </div>
          </div>
          <>
              {/* Course detail */}
              <section className="course-details-two">
                  <div className="container">
                      <div className="row mt-4">
                          <div className="col-lg-8">
                          <div>
                                  <BackendImage
                                      src={program?.image?.url}
                                      alt="img"
                                      className="img-fluid mb-4"
                                      fallback="/assets/img/course-placeholder.png"
                                  />
                              </div>
                              <div className="course-page-content pt-0">
                                  <div className="card mb-4">
                                      <div className="card-body">
                                          <h5 className="mb-3">Overview</h5>
                                          <h6 className="mb-2">
                                              Program Description
                                          </h6>
                                          {/* <div
                                              dangerouslySetInnerHTML={{
                                                  __html: DOMPurify.sanitize(
                                                      renderLexicalRichText(
                                                          program?.about || ""
                                                      )
                                                  ),
                                              }}
                                          /> */}
                                          {program?.about || ""}
                                      </div>
                                  </div>
                              </div>
                              <div className="course-page-content pt-0">
                                  <div className="card mb-4">
                                      <div className="card-body">
                                          <h5 className="mb-3">
                                              Program Requirements
                                          </h5>
                                          {/* <div
                                              dangerouslySetInnerHTML={{
                                                  __html: DOMPurify.sanitize(
                                                      renderLexicalRichText(
                                                          program?.requirement ||
                                                              ""
                                                      )
                                                  ),
                                              }}
                                          /> */}
                                          {program?.requirement||""}
                                      </div>
                                  </div>
                              </div>
                          </div>
                          <div className="col-lg-4">
                              <div className="course-sidebar-sec mt-0">
                                  <div className="card mb-4">
                                      <button
                                          className="btn btn-secondary font-heading icon-send-letter"
                                          onClick={() => {
                                              if (!isAuthenticated) {
                                                  window.location.href = all_routes.login;
                                                  return;
                                              }
                                              setSelectedProgram(program);
                                              setApplyPopup(true);
                                          }}
                                      >
                                          Apply for the Program
                                      </button>
                                  </div>

                                  <div className="card mb-4">
                                      <div className="card-body">
                                          <h5 className="subs-title mb-4">
                                              Program Details
                                          </h5>
                                          <p className="mb-3">
                                              Program Level:{" "}
                                              {program?.programLevel || ""}
                                          </p>
                                          <p className="mb-3">
                                              Application Fee:{" "}
                                              {program?.applicationFee
                                                  ? `${getCurrencySymbol(program.institution?.country)}${program.applicationFee}`
                                                  : "Free"}
                                          </p>
                                          <p className="mb-3">
                                              Tuition Fee (First year):{" "}
                                              {program?.tuitionFee
                                                  ? `${getCurrencySymbol(program.institution?.country)}${program.tuitionFee}`
                                                  : "Free"}
                                          </p>
                                          <p className="mb-3">
                                            Start Date: {formatDate(program?.start)}
                                            </p>

                                            <p className="mb-3">
                                            End Date: {formatDate(program?.end)}
                                            </p>

                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
                  <ApplyPopup
                      show={applyPopup}
                      onClose={() => setApplyPopup(false)}
                      program={selectedProgram}
                  />
              </section>
              {/* /Course detail */}
          </>
      </>
  );
};

export default ProgramDetails;
